import React, { Fragment, useCallback, useState } from "react";
import { Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Typography } from "@mui/material";
import { t } from "i18next";
import { DoneAll, Send } from "@mui/icons-material";
import { GetWhoAmIResponse } from "../../query/get-who-am-i/get-who-ami-i-response";
import { ConfirmSupplierContactDataValidation } from "./cqrs/confirm-supplier-contact-data-validation/confirm-supplier-contact-data-validation";
import { notificationModel } from "../../../common/component/notifications/notification-model";
import { ReportInvalidSupplierContactData } from "./cqrs/report-invalid-supplier-contact-data/report-invalid-supplier-contact-data";

interface ISupplierContactDataFormProps {
    isVoluntary: boolean;
    supplierData: GetWhoAmIResponse;
    onValidated: () => void;
    onReportCreated: (ticketNumber: string) => void;
    onClose?: () => void;
}

export const SupplierContactDataForm = (props: ISupplierContactDataFormProps) => {
    const [isDataSubmitting, setIsDataSubmitting] = useState(false);

    const [hasIncorrectData, setHasIncorrectData] = useState<boolean>(false);
    const [reportCommentFormValue, setReportCommentFormValue] = useState<string>("");

    const handleConfirmContactData = useCallback(async () => {
        try {
            setIsDataSubmitting(true);
            await ConfirmSupplierContactDataValidation();
            props.onValidated();
        } catch (error) {
            notificationModel.addSuccessMessage(t("components.supplierValidationPopup.error"));
        } finally {
            setIsDataSubmitting(false);
        }
    }, [props.onValidated]);

    const handleSubmitReport = useCallback(async () => {
        try {
            setIsDataSubmitting(true);
            props.onReportCreated(await ReportInvalidSupplierContactData(reportCommentFormValue));
        } catch (error) {
            notificationModel.addErrorMessage(t("components.supplierValidationPopup.error"));
        } finally {
            setIsDataSubmitting(false);
        }
    }, [reportCommentFormValue, props.onReportCreated]);

    return <Fragment>
        <DialogTitle>
            {t("components.supplierValidationPopup.title")}
        </DialogTitle>
        <DialogContent>
            <Typography>
                {t(props.isVoluntary ?
                    "components.supplierValidationPopup.descriptionVoluntary" :
                    "components.supplierValidationPopup.description")}
            </Typography>
            <div className={"supplier-contact-data"}>
                <div className={"supplier-contact-data__row"}>
                    <Typography className={"supplier-contact-data__label"}>
                        {t("components.supplierValidationPopup.form.email")}
                    </Typography>
                    <Typography className={"supplier-contact-data__value"}>
                        {props.supplierData.email}
                    </Typography>
                </div>
                <div className={"supplier-contact-data__row"}>
                    <Typography className={"supplier-contact-data__label"}>
                        {t("components.supplierValidationPopup.form.contactData")}
                    </Typography>
                    <Typography className={"supplier-contact-data__value"}>
                        {props.supplierData.contactData || <em>{t("components.supplierValidationPopup.form.noDetails")}</em>}
                    </Typography>
                </div>
                <div className={"supplier-contact-data__row"}>
                    <Typography className={"supplier-contact-data__label"}>
                        {t("components.supplierValidationPopup.form.warehouse")}
                    </Typography>
                    <Typography className={"supplier-contact-data__value"}>
                        {props.supplierData.warehouse.name}
                    </Typography>
                </div>
                <div className={"supplier-contact-data__row"}>
                    <Typography className={"supplier-contact-data__label"}>
                        {t("components.supplierValidationPopup.form.warehouseAddress")}
                    </Typography>
                    <Typography className={"supplier-contact-data__value"}>
                        {props.supplierData.warehouse.fullAddress}
                    </Typography>
                </div>
            </div>
            <FormControlLabel key={`report-incorrect-data-checkbox`}
                              label={t("components.supplierValidationPopup.form.correctData")}
                              control={<Checkbox color={"error"} checked={hasIncorrectData}
                                                 onChange={(_, checked) => {
                                                     setHasIncorrectData(checked); }} />} />
            {hasIncorrectData &&
                <TextField multiline placeholder={t("components.supplierValidationPopup.form.correctDataReportPlaceholder")}
                           value={reportCommentFormValue}
                           onChange={e => setReportCommentFormValue(e.target.value)}
                           rows={3} fullWidth={true} />}
        </DialogContent>
        <DialogActions>
            {props.onClose && <Button variant={"contained"} color={"inherit"} onClick={props.onClose}>
                {t("components.supplierValidationPopup.cancel")}
            </Button>}
            {!hasIncorrectData ?
                <Button variant={"contained"} color={"secondary"} onClick={handleConfirmContactData} endIcon={<DoneAll />}
                        loading={isDataSubmitting} loadingPosition={"end"}>
                    {t("components.supplierValidationPopup.submitConfirm")}
                </Button> :
                <Button variant={"contained"} color={"error"} onClick={handleSubmitReport} endIcon={<Send />}
                        disabled={reportCommentFormValue === ""}
                        loading={isDataSubmitting} loadingPosition={"end"}>
                    {t("components.supplierValidationPopup.submitReport")}
                </Button>}
        </DialogActions>
    </Fragment>;
};
